import writeXlsxFile, { type Schema } from 'write-excel-file';

import type { AdhesionProposalItem } from '../types/vida/table/adhesionProposalTableType';
import { formatAdhesionProposalSubestipulante } from './adhesionProposalUtil';
import { mapProponentStatus } from './proponentStatusUtils';

/**
 * Sanitizes a string for use in filename
 */
const sanitizeFileName = (str: string): string => {
  return str.replace(/[^a-zA-Z0-9]/g, '_');
};

/**
 * Generates a filename with current date
 */
const generateFileNameWithDate = (prefix: string, extension: string = 'xlsx'): string => {
  const currentDate = new Date().toISOString().split('T')[0].replace(/-/g, '');
  const sanitizedPrefix = sanitizeFileName(prefix);
  return `${sanitizedPrefix}_${currentDate}.${extension}`;
};

type ProposalRow = {
  id: number;
  status: string;
  statusDate: string;
  cpf: string;
  proposer: string;
  prazoDecurso: string;
  subCompany: string;
  contractNumber: string;
};

/**
 * Generates an XLSX file for adhesion proposals and downloads it automatically
 * @param proposals - Array of proposal data to export
 * @param formatDate - Function to format dates
 * @param formatCpf - Function to format CPF
 * @param mapStatus - Optional function to map status to readable format
 */
export const generateProposalsXLSX = async (
  proposals: AdhesionProposalItem[],
  formatDate: (dateStr: string) => string,
  formatCpf: (cpf: string) => string,
  mapStatus?: (status: string) => string
): Promise<void> => {
  if (!proposals || proposals.length === 0) {
    return;
  }

  const statusMapper = mapStatus || mapProponentStatus;

  const excelData: ProposalRow[] = proposals.map((proposal) => ({
    id: proposal.id,
    status: statusMapper(proposal.status),
    statusDate: formatDate(proposal.statusAt),
    cpf: formatCpf(proposal.proposerCpf),
    proposer: proposal.proposerName,
    prazoDecurso: proposal.prazoDec ? `${proposal.prazoDec} dias` : '-',
    subCompany: formatAdhesionProposalSubestipulante(proposal),
    contractNumber: proposal.contractNumber,
  }));

  const schema: Schema<ProposalRow> = [
    { column: 'ID', type: Number, width: 10, value: (row) => row.id },
    { column: 'Status', type: String, width: 25, value: (row) => row.status },
    { column: 'Data do Status', type: String, width: 18, value: (row) => row.statusDate },
    { column: 'CPF', type: String, width: 15, value: (row) => row.cpf },
    { column: 'Proponente', type: String, width: 30, value: (row) => row.proposer },
    { column: 'Prazo Decurso', type: String, width: 15, value: (row) => row.prazoDecurso },
    { column: 'Sub-Estipulante', type: String, width: 25, value: (row) => row.subCompany },
    { column: 'Número do Contrato', type: String, width: 20, value: (row) => row.contractNumber },
  ];

  await writeXlsxFile(excelData, {
    schema,
    fileName: generateFileNameWithDate('Propostas_Adesao'),
  });
};

